import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { clientService } from '../../../services';
import { PARTNER_LOGIN_URL } from 'config/partnerPortal';

const EmailVerification = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLinkExpired, setIsLinkExpired] = useState(false);
  const [responseMessage, setResponseMessage] = useState({ showBtn: true, message: '' });

  const { search, pathname } = useLocation();

  const hasRun = useRef(false);  // Ref to ensure handleUpdate only runs once

  // Function to validate if the URL path and token query are correct
  const isValidPath = useCallback(() => {
    const isEmailVerifiedPath = pathname.startsWith('/emailVerified/');
    const hasToken = search.includes('?token=');
    return isEmailVerifiedPath && hasToken;
  }, [pathname, search]);

  const handleUpdate = useCallback(async () => {
    setIsLoading(true);
    setError(null); // Reset error

    try {
      let postClient;

      // Example of the clientService call to update client details
      postClient = await clientService.updateLeadInfo(search.split('?token=')[1]);

      // If message indicates the link is expired, handle accordingly
      if (postClient?.status === 202) {
        setIsLinkExpired(true);
        setResponseMessage({
          showBtn: false,
          message: postClient?.data?.message
        })
      } else if (postClient?.status === 201) {
        setIsLinkExpired(true);
        setResponseMessage({
          showBtn: false,
          message: postClient?.data?.message
        });
      } else {
        setIsLinkExpired(false);
        setResponseMessage({
          showBtn: true,
          message: postClient?.data?.message
        });
      }

    } catch (err) {
      console.error('Error during verification:', err);
      setError('There was an error verifying your email. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (isValidPath() && !hasRun.current) {
      hasRun.current = true;  // Mark as run
      handleUpdate();
    }
  }, [handleUpdate, isValidPath]);

  // If the URL path is not valid, show "Page Not Found"
  if (!isValidPath()) {
    return (
      <div
        style={{
          backgroundColor: '#ffffff',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            backgroundColor: '#ffffff',
            padding: '40px',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 6px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <h2 style={{ color: 'red' }}>Page Not Found</h2>
          <p>The page you are looking for does not exist or the link is invalid.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          padding: '40px',
          borderRadius: '8px',
          maxWidth: '600px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div style={{ marginBottom: '30px' }}>
          <img
            src="https://www.godocm.com/wp-content/uploads/2025/05/cropped-GoDo-Logo-7-1.png"
            alt="Company Logo"
            width="100"
            style={{ maxWidth: '100px' }}
          />
        </div>

        {isLoading ? (
          <div style={{ fontSize: '16px', color: '#555' }}>
            <div className="loader"></div> {/* The loader */}
            <p style={{ marginTop: '10px' }}>Please wait while we complete the verification...</p>
          </div>
        ) : error ? (
          <p style={{ fontSize: '16px', color: 'red' }}>{error}</p>
        ) : isLinkExpired ? (
          <div>
            <p style={{ fontSize: '16px', color: 'red' }}>
              {responseMessage.message}
            </p>
          </div>
        ) : (
          <div style={{ fontSize: '16px', color: '#555' }}>
            <h5 className='text-bold'>🎉 You're All Set! 🎉</h5>
            <h4 className='text-bold'>✅ Account Verified Successfully!</h4>
            <p>{responseMessage.message}</p>
            <p>If you don’t see the email soon, check your <b>spam or junk folder</b> just in case.</p>
            <p>💡 <b>Need help?</b> Contact us anytime!</p>
            <p>📩 Email: <a href="mailto:cs@godocm.com">cs@godocm.com</a></p>
            <p className='text-bold'>✨ Welcome to the GODO family</p>
          </div>
        )}

        {/* Conditionally render the Login Button or Expired message */}
        {!isLinkExpired && !isLoading && !error && responseMessage.showBtn && (
          <div style={{ marginTop: '30px' }}>
            <a
              href={PARTNER_LOGIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                backgroundColor: '#1a82e2',
                color: '#fff',
                padding: '12px 30px',
                textDecoration: 'none',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: 'bold',
              }}
            >
              Login to IB Portal
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailVerification;